import {
  CheckEmailDomainResponse,
  CreateSubscriptionArgs,
  NiceLabelInstancesResponse,
  Subscription,
  SubscriptionsResponse,
  SubscriptionsSearchParams,
  checkUniqueInstanceName,
  UpcomingRenewal,
  DownloadCsvSearchParams,
  NiceLabelInstance,
  CancelSubscriptionArgs,
  CouponResponse,
  ValidateCouponRequest,
  RenewalCoupon,
  RenewalCouponDiscountType,
} from '~/types/subscriptions'
import { ApiKy } from './index'
import { InvoicesResponse } from '~/types/invoices'
import {
  CreditNotesResponse,
  PartnerCreditNotesSearchParams,
} from '~/types/credit-notes'
import { SearchParams } from '~/types/pagination'

const { stripEmptyKeys } = useHelpers()

export default class Api extends ApiKy {
  get(searchParams: SubscriptionsSearchParams, signal?: AbortSignal) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(this.url, { searchParams: params, signal })
      ?.json<SubscriptionsResponse>()
  }

  getSubscriptionInvoices(
    subscriptionId: string,
    searchParams: SubscriptionsSearchParams,
  ) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(`${this.url}/${subscriptionId}/invoices`, { searchParams: params })
      ?.json<InvoicesResponse>()
  }

  getSubscriptionCreditNotes(
    subscriptionId: string,
    searchParams: PartnerCreditNotesSearchParams,
  ) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(`${this.url}/${subscriptionId}/credit-notes`, {
        searchParams: params,
      })
      ?.json<CreditNotesResponse>()
  }

  getSingle(subscriptionId: string, searchParams: SearchParams = {}) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(`${this.url}/${subscriptionId}`, { searchParams: params })
      ?.json<Subscription>()
  }

  getNiceLabelInstances(
    subscriptionId: string,
    searchParams: SearchParams = {},
  ) {
    const params = stripEmptyKeys(searchParams)
    return this.ky
      .get(`${this.url}/${subscriptionId}/nicelabel-instances`, {
        searchParams: params,
      })
      ?.json<NiceLabelInstancesResponse>()
  }

  createSubscription<T>(payload: T) {
    return this.ky.post(this.url, { json: payload })?.json<Subscription>()
  }

  update(subscriptionId: string, payload: Partial<CreateSubscriptionArgs>) {
    return this.ky.patch(`${this.url}/${subscriptionId}`, { json: payload })
  }

  activate(subscriptionId: string, payload: Partial<CreateSubscriptionArgs>) {
    return this.ky.post(`${this.url}/${subscriptionId}/activate`, {
      json: payload,
    })
  }

  extendTrial(subscriptionId: string) {
    return this.ky.post(`${this.url}/${subscriptionId}/extend-trial`)
  }

  cancelSubscription(subscriptionId: string, payload?: CancelSubscriptionArgs) {
    return this.ky.post(`${this.url}/${subscriptionId}/cancel`, {
      json: payload,
    })
  }

  checkEmailDomain(emailDomain: string) {
    return this.ky
      .post(`${this.url}/check-email-domain`, {
        json: { emailDomain },
      })
      ?.json<CheckEmailDomainResponse>()
  }

  checkUniqueInstanceName(instanceName: string) {
    return this.ky
      .post(`${this.url}/unique-instance-name`, {
        json: { instanceName },
      })
      ?.json<checkUniqueInstanceName>()
  }

  adjustPrinters(
    subscriptionId: string,
    numberOfPrinters: number,
    productOrderNumber: string,
  ) {
    return this.ky.post(`${this.url}/${subscriptionId}/adjust-printers`, {
      json: { numberOfPrinters, productOrderNumber },
    })
  }

  updateSubscription<T>(subscriptionId: string, payload: T) {
    return this.ky.post(`${this.url}/${subscriptionId}/update-plan`, {
      json: payload,
    })
  }

  getUpcomingRenewals(partnerId: string) {
    return this.ky
      .get(`${this.url}/upcoming-renewals`, {
        searchParams: { partnerId },
      })
      ?.json<UpcomingRenewal[]>()
  }

  downloadCsv(searchParams: DownloadCsvSearchParams) {
    const params = stripEmptyKeys(searchParams)
    return this.ky.get(`${this.url}/download-csv`, {
      searchParams: params,
    })
  }

  resendInvites(subscriptionId: string, instanceId: string) {
    return this.ky.post(
      `${this.url}/${subscriptionId}/nicelabel-instances/${instanceId}/resend-invites`,
    )
  }

  addInviteEmails(subscriptionId: string, instanceId: string, emails: string) {
    return this.ky.post(
      `${this.url}/${subscriptionId}/nicelabel-instances/${instanceId}/add-invite-emails`,
      { json: { emails } },
    )
  }

  updateNiceLabelInstance(
    subscriptionId: string,
    instanceId: string,
    payload: Partial<NiceLabelInstance>,
  ) {
    return this.ky.patch(
      `${this.url}/${subscriptionId}/nicelabel-instances/${instanceId}`,
      { json: payload },
    )
  }

  createRenewalCoupon(
    subscriptionId: string,
    payload: {
      discountType: RenewalCouponDiscountType
      discountValue: number
    },
  ) {
    return this.ky
      .post(`${this.url}/${subscriptionId}/renewal-coupons`, {
        json: payload,
      })
      ?.json<RenewalCoupon>()
  }

  deleteRenewalCoupon(subscriptionId: string, couponId: string) {
    return this.ky.delete(
      `${this.url}/${subscriptionId}/renewal-coupons/${couponId}`,
    )
  }

  restoreRenewalCoupon(subscriptionId: string, couponId: string) {
    return this.ky.post(
      `${this.url}/${subscriptionId}/renewal-coupons/${couponId}/restore`,
    )
  }

  getRenewalCoupons(subscriptionId: string) {
    return this.ky
      .get(`${this.url}/${subscriptionId}/renewal-coupons`)
      ?.json<RenewalCoupon[]>()
  }

  validateCoupon({
    couponId,
    partnerId,
    estimateRequest,
  }: ValidateCouponRequest) {
    return this.ky
      .post(`${this.url}/validate-coupon`, {
        json: { couponId, partnerId, estimateRequest },
      })
      ?.json<CouponResponse>()
  }

  validateCouponForSubscription(subscriptionId: string, couponId: string) {
    return this.ky
      .post(`${this.url}/${subscriptionId}/validate-coupon`, {
        json: { couponId },
      })
      ?.json<CouponResponse>()
  }

  applyCoupon(subscriptionId: string, couponId: string) {
    return this.ky.post(`${this.url}/${subscriptionId}/apply-coupon`, {
      json: { couponId },
    })
  }

  syncPrinterSeats(subscriptionId: string) {
    return this.ky.post(`${this.url}/${subscriptionId}/sync-printer-seats`)
  }

  overridePrinterSeats(subscriptionId: string, numberOfPrinters: number) {
    return this.ky.post(
      `${this.url}/${subscriptionId}/override-printer-seats`,
      {
        json: { numberOfPrinters },
      },
    )
  }
}
